/**
 * WorklistDropdown — reusable flat 2px selector for switching between a
 * caller-supplied list of worklists/batches. Mirrors mockup `#s-astudy`
 * `.btn-sel` / `.wmenu`
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html).
 *
 * Presentational + local open/close state only — no data fetching. Callers
 * (StudyView's worklist switcher, AnnotatorCategory's resume-hero "Study
 * view" control) build `worklists` from whatever source is relevant and get
 * the selected id back via `onSelect`.
 *
 * NON-NEGOTIABLE: radius is `rounded` (2px) only — never `rounded-lg` /
 * `rounded-full`. See primitives.tsx for the same rule.
 */

import { useEffect, useRef, useState } from "react";

import { StatusBadge, type StatusTone } from "./primitives";

export interface WorklistDropdownItem {
  id: string;
  label: string;
  tag?: string;
  tone?: StatusTone;
}

interface WorklistDropdownProps {
  worklists: WorklistDropdownItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label?: string;
}

export function WorklistDropdown({
  worklists,
  selectedId,
  onSelect,
  label = "Worklist",
}: WorklistDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleDocumentMouseDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
  }, [open]);

  const selected = worklists.find((w) => w.id === selectedId);

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded border border-border-strong bg-surface-2 px-3.5 py-2.5 transition hover:bg-white/[0.04]"
      >
        <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-fg-faint">
          {label}
        </span>
        <span className="font-mono text-[12.5px] text-fg">
          {selected?.label ?? "Select a worklist"}
        </span>
        <span aria-hidden="true" className="text-[11px] text-fg-faint">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[280px] overflow-hidden rounded border border-border-strong bg-surface-2 shadow-lg">
          {worklists.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setOpen(false);
                onSelect(item.id);
              }}
              className={`flex w-full items-center gap-2.5 border-b border-border/50 px-3.5 py-2.5 text-left transition last:border-b-0 hover:bg-white/[0.04] ${
                item.id === selectedId ? "bg-signal-tint" : ""
              }`}
            >
              <span className="font-mono text-xs text-fg">{item.label}</span>
              <span className="flex-1" />
              {item.tag && <StatusBadge tone={item.tone ?? "neutral"}>{item.tag}</StatusBadge>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default WorklistDropdown;
